/**
 * Frontend Structured Logger
 * 
 * Industry-standard logging utility for frontend applications.
 * - Environment-aware (verbose in dev, minimal in production)
 * - Structured format for log aggregation
 * - Error boundary integration
 * - Performance timing support
 * 
 * Usage:
 *   import { logger } from './utils/logger';
 *   logger.info('User logged in', { userId: '123' });
 *   logger.error('API call failed', { error: err.message });
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogMeta {
  [key: string]: unknown;
  component?: string;
  error?: string;
  stack?: string;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3
};

// Get log level from environment
const getCurrentLogLevel = (): LogLevel => {
  const envLevel = process.env.REACT_APP_LOG_LEVEL?.toLowerCase() as LogLevel;
  if (envLevel && LOG_LEVELS[envLevel] !== undefined) {
    return envLevel;
  }
  return process.env.NODE_ENV === 'production' ? 'warn' : 'debug';
};

const shouldLog = (level: LogLevel): boolean => {
  const currentLevel = getCurrentLogLevel();
  return LOG_LEVELS[level] >= LOG_LEVELS[currentLevel];
};

// Format log entry
const formatLogEntry = (level: LogLevel, message: string, meta?: LogMeta): string => {
  const timestamp = new Date().toISOString();
  const metaStr = meta ? ` ${JSON.stringify(meta)}` : '';
  return `[${timestamp}] [${level.toUpperCase()}] ${message}${metaStr}`;
};

// Core logging function
const log = (level: LogLevel, message: string, meta?: LogMeta): void => {
  if (!shouldLog(level)) return;

  const formattedMessage = formatLogEntry(level, message, meta);

  switch (level) {
    case 'error':
      console.error(formattedMessage);
      break;
    case 'warn':
      console.warn(formattedMessage);
      break;
    case 'debug':
      // eslint-disable-next-line no-console
      console.debug(formattedMessage);
      break;
    default:
      // eslint-disable-next-line no-console
      console.log(formattedMessage);
  }
};

/**
 * Logger interface
 */
/**
 * Safely extract error message from unknown error type
 * Handles Error objects, strings, and other types
 */
export const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return String(error);
};

export const logger = {
  /**
   * Log debug message (development only by default)
   */
  debug: (message: string, meta?: LogMeta): void => log('debug', message, meta),

  /**
   * Log info message
   */
  info: (message: string, meta?: LogMeta): void => log('info', message, meta),

  /**
   * Log warning message
   */
  warn: (message: string, meta?: LogMeta): void => log('warn', message, meta),

  /**
   * Log error message
   */
  error: (message: string, meta?: LogMeta): void => log('error', message, meta),

  /**
   * Create a child logger with preset context
   */
  child: (context: LogMeta) => ({
    debug: (message: string, meta?: LogMeta) => log('debug', message, { ...context, ...meta }),
    info: (message: string, meta?: LogMeta) => log('info', message, { ...context, ...meta }),
    warn: (message: string, meta?: LogMeta) => log('warn', message, { ...context, ...meta }),
    error: (message: string, meta?: LogMeta) => log('error', message, { ...context, ...meta }),
  }),

  /**
   * Start a timer for performance logging
   */
  startTimer: (label: string) => {
    const start = Date.now();
    return (meta?: LogMeta): number => {
      const duration = Date.now() - start;
      log('debug', `${label} completed`, { duration: `${duration}ms`, ...meta });
      return duration;
    };
  },

  /**
   * Log API call
   */
  api: (method: string, url: string, statusCode: number, duration: number): void => {
    const level: LogLevel = statusCode >= 500 ? 'error' : statusCode >= 400 ? 'warn' : 'debug';
    log(level, `API ${method} ${url}`, { statusCode, duration: `${duration}ms` });
  },

  /**
   * Log user action
   */
  action: (action: string, meta?: LogMeta): void => {
    log('info', `User action: ${action}`, meta);
  },

  /**
   * Log component lifecycle (development only)
   */
  component: (componentName: string, event: 'mount' | 'unmount' | 'render' | 'error', meta?: LogMeta): void => {
    if (process.env.NODE_ENV === 'development') {
      log('debug', `Component ${componentName} ${event}`, meta);
    }
  },
};

export default logger;

